import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import BlockIcon from "@mui/icons-material/Block";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import PersonIcon from "@mui/icons-material/Person";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EmailIcon from "@mui/icons-material/Email";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import StarIcon from "@mui/icons-material/Star";
import LinkIcon from "@mui/icons-material/Link";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { getApiUrl } from "../config/api";
import { TabProvider, useTabContext } from "../contexts/TabContext";
import { getPathToLabel, preloadPageForPath } from "../config/pathConfig";
import ContentTabs from "./ContentTabs";
import TabContent from "./TabContent";
import LocationSync from "./LocationSync";

const drawerWidth = 80;

/** Google 品牌四色（主题色） */
const GOOGLE_ACCENT = {
  blue: "#4285F4",
  green: "#34A853",
  yellow: "#FBBC05",
  red: "#EA4335",
};

/** 未选中时侧栏图标统一灰阶 */
const NAV_ICON_IDLE = "#9aa0a6";

/** 侧栏菜单图标相对原尺寸（24/22px）整体约放大 5% */
const NAV_ICON_SCALE = 1.05;
const NAV_ICON_PX_SELECTED = 24 * NAV_ICON_SCALE;
const NAV_ICON_PX_IDLE = 22 * NAV_ICON_SCALE;
const NAV_CREATE_ADD_PX = 24 * NAV_ICON_SCALE;

/** Google「G」标识；未选中时灰度，选中时恢复四色 */
const GoogleAdsIcon = ({ selected, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    width={24 * NAV_ICON_SCALE}
    height={24 * NAV_ICON_SCALE}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{
      filter: selected ? "none" : "grayscale(1)",
      opacity: selected ? 1 : 0.55,
    }}
    {...props}
  >
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const menuItems = [
  { text: "Campaigns", Icon: DashboardIcon, path: "/dashboard", accent: GOOGLE_ACCENT.blue },
  { text: "黑名单", Icon: BlockIcon, path: "/blacklist", adminOnly: true, accent: GOOGLE_ACCENT.green },
  {
    text: "Google Ads",
    Icon: GoogleAdsIcon,
    path: "/ads-records",
    adsPermissionOnly: true,
    accent: GOOGLE_ACCENT.yellow,
    multiColorGoogleIcon: true,
  },
  { text: "账户列表", Icon: CampaignIcon, path: "/google-ads", accent: GOOGLE_ACCENT.red },
  {
    text: "空闲账号",
    Icon: EventAvailableIcon,
    path: "/idle-accounts",
    adminOnly: true,
    accent: GOOGLE_ACCENT.blue,
  },
  { text: "邮箱管理", Icon: EmailIcon, path: "/email-management", accent: GOOGLE_ACCENT.green },
  { text: "配置信息", Icon: SettingsIcon, path: "/settings", accent: GOOGLE_ACCENT.yellow },
  {
    text: "权限分配",
    Icon: AdminPanelSettingsIcon,
    path: "/permission-management",
    adminOnly: true,
    accent: GOOGLE_ACCENT.red,
  },
  { text: "个人中心", Icon: PersonIcon, path: "/profile", accent: GOOGLE_ACCENT.blue },
  {
    text: "收藏Offer",
    Icon: StarIcon,
    path: "/recommended-offers",
    accent: GOOGLE_ACCENT.yellow,
  },
  {
    text: "Post Back",
    Icon: LinkIcon,
    path: "/post-back",
    accent: GOOGLE_ACCENT.green,
  },
];

const renderMenuIcon = (item, isSelected) => {
  const IconComponent = item.Icon;
  if (item.multiColorGoogleIcon) {
    return <IconComponent selected={isSelected} />;
  }
  return <IconComponent />;
};

function LayoutContent() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userPhotoURL, setUserPhotoURL] = useState("");
  const [userDisplayName, setUserDisplayName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole, canManageAdsCampaign } = useAuth();
  const { addOrSwitchTab } = useTabContext();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileClick = useCallback(() => {
    addOrSwitchTab("/profile", "个人中心");
    navigate("/profile");
  }, [addOrSwitchTab, navigate]);

  const handleCreateOffer = () => {
    addOrSwitchTab("/dashboard", "Campaigns");
    if (location.pathname === "/dashboard") {
      navigate(`/dashboard?create=true&t=${Date.now()}`, { replace: false });
    } else {
      navigate("/dashboard?create=true");
    }
    setMobileOpen(false);
  };

  // 获取用户头像和显示名称（只在首次加载和从profile页面返回时调用）
  const fetchUserInfoRef = useRef(null);

  // 使用 useCallback 但不在依赖项中使用，避免不必要的重新执行
  const fetchUserInfo = useCallback(async () => {
    if (!currentUser) {
      setUserPhotoURL("");
      setUserDisplayName("");
      return;
    }

    try {
      // 首先尝试从 currentUser 获取（如果 Firebase Auth 已更新）
      if (currentUser.photoURL) {
        setUserPhotoURL(currentUser.photoURL);
      }
      if (currentUser.displayName) {
        setUserDisplayName(currentUser.displayName);
      }

      // 尝试从 localStorage 加载缓存
      const { getCachedUserInfo, setCachedUserInfo } =
        await import("../utils/persistentCache");
      const cachedUserInfo = getCachedUserInfo();

      if (cachedUserInfo) {
        console.log("[用户信息] Layout 使用 localStorage 缓存");
        if (cachedUserInfo.photoURL) {
          setUserPhotoURL(cachedUserInfo.photoURL);
        }
        if (cachedUserInfo.displayName) {
          setUserDisplayName(cachedUserInfo.displayName);
        } else {
          setUserDisplayName("");
        }
        return;
      }

      // 缓存不存在，从 API 获取完整用户信息
      console.log("[用户信息] Layout 从 API 获取数据...");
      const response = await axios.get(getApiUrl("/api/user"), {
        timeout: 10000,
      });
      const userInfo = response.data;

      if (userInfo?.photoURL) {
        setUserPhotoURL(userInfo.photoURL);
      }
      if (userInfo?.displayName) {
        setUserDisplayName(userInfo.displayName);
      } else {
        setUserDisplayName("");
      }

      // 缓存用户信息
      setCachedUserInfo(userInfo);
      console.log("[用户信息] Layout 数据已缓存到 localStorage");
    } catch (err) {
      console.error("获取用户信息失败:", err);
      // 静默失败，使用默认logo
      setUserPhotoURL("");
      setUserDisplayName("");
    }
  }, [currentUser]);

  // 更新 ref，确保总是使用最新的函数
  fetchUserInfoRef.current = fetchUserInfo;

  // 只在非 profile 页面时获取用户信息，避免与 Profile 组件重复调用
  const hasInitializedRef = useRef(false);
  const prevPathnameRef = useRef(location.pathname);
  const prevUserIdRef = useRef(currentUser?.uid);

  useEffect(() => {
    // 如果当前在 profile 页面，不调用（Profile 组件会自己调用）
    if (location.pathname === "/profile") {
      prevPathnameRef.current = location.pathname;
      prevUserIdRef.current = currentUser?.uid;
      return;
    }

    if (!currentUser) {
      setUserPhotoURL("");
      setUserDisplayName("");
      hasInitializedRef.current = false;
      prevPathnameRef.current = location.pathname;
      prevUserIdRef.current = null;
      return;
    }

    // 如果用户ID变化了（比如重新登录），重置初始化状态
    if (prevUserIdRef.current !== currentUser.uid) {
      hasInitializedRef.current = false;
      prevUserIdRef.current = currentUser.uid;
    }

    // 首次加载时调用，或者从 profile 页面返回时调用
    const isReturningFromProfile =
      prevPathnameRef.current === "/profile" &&
      location.pathname !== "/profile";

    // 只在首次加载、用户变化、或从 profile 返回时调用
    if (!hasInitializedRef.current || isReturningFromProfile) {
      // 从 profile 返回时延迟一下，确保数据已更新
      const timer = setTimeout(
        () => {
          if (fetchUserInfoRef.current) {
            fetchUserInfoRef.current();
          }
        },
        isReturningFromProfile ? 500 : 0,
      );
      hasInitializedRef.current = true;
      prevPathnameRef.current = location.pathname;
      return () => clearTimeout(timer);
    }

    // 更新路径引用，但不调用接口
    prevPathnameRef.current = location.pathname;
  }, [currentUser, location.pathname]); // 移除 fetchUserInfo 依赖，避免不必要的重新执行

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f8f9fa",
        borderRight: "1px solid #e0e0e0",
      }}
    >
      {/* 创建按钮 */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Tooltip title="新建Campaign" placement="right" arrow>
          <Button
            variant="contained"
            onClick={handleCreateOffer}
            sx={{
              minWidth: 0,
              width: 40,
              height: 40,
              borderRadius: "50%",
              p: 0,
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              transition: "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                transform: "scale(1.2) rotate(180deg)",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)",
                "& svg": {
                  transform: "rotate(180deg) scale(1.2)",
                },
              },
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              "& svg": {
                transition: "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
              },
            }}
          >
            <AddIcon sx={{ fontSize: NAV_CREATE_ADD_PX }} />
          </Button>
        </Tooltip>
      </Box>

      {/* 菜单项：未选中约缩小 10%，选中恢复原先尺寸 */}
      <List sx={{ flex: 1, px: 0.5, py: 0.9 }}>
        {menuItems
          .filter(
            (item) =>
              (!item.adminOnly || userRole === 0) &&
              (!item.adsPermissionOnly || canManageAdsCampaign),
          )
          .map((item) => {
            const isSelected = location.pathname === item.path;
            return (
              <ListItem
                key={item.path}
                disablePadding
                sx={{ mb: isSelected ? 0.5 : 0.45 }}
              >
                <ListItemButton
                  selected={isSelected}
                  onMouseEnter={() => preloadPageForPath(item.path)}
                  onClick={() => {
                    addOrSwitchTab(item.path, item.text);
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                  aria-label={item.text}
                  sx={{
                    minHeight: isSelected ? 64 : 58,
                    borderRadius: 2,
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 0,
                    py: isSelected ? 1 : 0.9,
                    transition:
                      "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                    "&.Mui-selected": {
                      backgroundColor: alpha(item.accent, 0.12),
                      color: item.accent,
                      "&:hover": {
                        backgroundColor: alpha(item.accent, 0.16),
                      },
                      "& .MuiListItemIcon-root": {
                        color: item.multiColorGoogleIcon ? "inherit" : item.accent,
                      },
                    },
                    "&:hover": {
                      backgroundColor: isSelected
                        ? alpha(item.accent, 0.16)
                        : "#f5f5f5",
                      transform: "translateY(-2px)",
                      "& .MuiListItemIcon-root": {
                        transform: "scale(1.3) translateY(-3px)",
                        "& svg": {
                          transform: "scale(1.2) rotate(15deg)",
                        },
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: "center",
                      mb: isSelected ? 0.5 : 0.45,
                      color: item.multiColorGoogleIcon
                        ? "inherit"
                        : isSelected
                          ? item.accent
                          : NAV_ICON_IDLE,
                      transition:
                        "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                      "& svg": {
                        fontSize: isSelected
                          ? `${NAV_ICON_PX_SELECTED}px`
                          : `${NAV_ICON_PX_IDLE}px`,
                        transition:
                          "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                      },
                    }}
                  >
                    {renderMenuIcon(item, isSelected)}
                  </ListItemIcon>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: isSelected ? "0.7rem" : "0.63rem",
                      fontWeight: isSelected ? 500 : 400,
                      color: isSelected ? item.accent : "#5f6368",
                      textAlign: "center",
                      lineHeight: 1.2,
                      mt: isSelected ? 0.25 : 0.225,
                    }}
                  >
                    {item.text}
                  </Typography>
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* 移动端菜单按钮和用户菜单 */}
      <Box
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 1300,
          display: { xs: "flex", sm: "none" },
          gap: 1,
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ bgcolor: "background.paper", boxShadow: 2 }}
        >
          <MenuIcon />
        </IconButton>
      </Box>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 240,
            },
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#f8f9fa",
            }}
          >
            {/* 创建按钮 */}
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleCreateOffer}
                startIcon={<AddIcon />}
                sx={{
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                  },
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                新建Campaign
              </Button>
            </Box>

            {/* 菜单项：未选中约缩小 10%，选中恢复 */}
            <List sx={{ flex: 1, px: 1, py: 0.9 }}>
              {menuItems
                .filter(
                  (item) =>
                    (!item.adminOnly || userRole === 0) &&
                    (!item.adsPermissionOnly || canManageAdsCampaign),
                )
                .map((item) => {
                  const isSelected = location.pathname === item.path;
                  return (
                    <ListItem
                      key={item.path}
                      disablePadding
                      sx={{ mb: isSelected ? 0.5 : 0.45 }}
                    >
                      <ListItemButton
                        selected={isSelected}
                        onMouseEnter={() => preloadPageForPath(item.path)}
                        onClick={() => {
                          addOrSwitchTab(item.path, item.text);
                          navigate(item.path);
                          setMobileOpen(false);
                        }}
                        aria-label={item.text}
                        sx={{
                          minHeight: isSelected ? 48 : 43,
                          borderRadius: 2,
                          px: 2,
                          py: isSelected ? 1 : 0.9,
                          transition:
                            "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                          "&.Mui-selected": {
                            backgroundColor: alpha(item.accent, 0.12),
                            color: item.accent,
                            "&:hover": {
                              backgroundColor: alpha(item.accent, 0.16),
                            },
                            "& .MuiListItemIcon-root": {
                              color: item.multiColorGoogleIcon
                                ? "inherit"
                                : item.accent,
                            },
                          },
                          "&:hover": {
                            backgroundColor: isSelected
                              ? alpha(item.accent, 0.16)
                              : "#f5f5f5",
                            transform: "translateX(4px)",
                            "& .MuiListItemIcon-root": {
                              transform: "scale(1.25)",
                              "& svg": {
                                transform: "scale(1.15) rotate(15deg)",
                              },
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: isSelected
                              ? 40 * NAV_ICON_SCALE
                              : 36 * NAV_ICON_SCALE,
                            color: item.multiColorGoogleIcon
                              ? "inherit"
                              : isSelected
                                ? item.accent
                                : NAV_ICON_IDLE,
                            transition:
                              "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                            "& svg": {
                              fontSize: isSelected
                                ? `${NAV_ICON_PX_SELECTED}px`
                                : `${NAV_ICON_PX_IDLE}px`,
                              transition:
                                "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                            },
                          }}
                        >
                          {renderMenuIcon(item, isSelected)}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: isSelected ? "0.875rem" : "0.79rem",
                            fontWeight: isSelected ? 500 : 400,
                            color: isSelected ? item.accent : "text.primary",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
            </List>
          </Box>
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          p: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ContentTabs
          userPhotoURL={userPhotoURL}
          userDisplayName={userDisplayName}
          onProfileClick={handleProfileClick}
        />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            p: 2,
            pt: 1.5,
          }}
        >
          <TabContent />
        </Box>
      </Box>
    </Box>
  );
}

function Layout() {
  const location = useLocation();
  const pathToLabel = getPathToLabel();
  const initialPath =
    location.pathname === "/" ? "/dashboard" : location.pathname;

  return (
    <TabProvider pathToLabel={pathToLabel} initialPath={initialPath}>
      <LocationSync />
      <LayoutContent />
    </TabProvider>
  );
}

export default Layout;
